import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference } from '@/helpers';
import { PageHeader } from '@/modules/common';
import LoadingContainer from '@/modules/common/components/loading-container';
import { Routes } from '@/router/routes';

import { useTextStyleDetails } from '../api';
import { TextStyleForm } from '../components';
import { TextStyleItem } from '../types';
import { TextStyleFormValues } from '../validations';

export const TextStyleDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();
  const { details, isFetching, updateTextStyleMutation } = useTextStyleDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      ...details,
      updatedAt: format(new Date(details.updatedAt), 'yyyy-MM-dd'),
    };
  }, [details]);

  const updateTextStyle = async (values: TextStyleFormValues) => {
    try {
      const body = deepDifference<TextStyleItem>(values, defaultValues);
      await updateTextStyleMutation.mutateAsync({
        ...body,
        id: params.id,
      });
      toast.success(`Text style "${values.name}" updated successfully!`);
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <LoadingContainer loading={isFetching}>
      <TextStyleForm editMode onSubmit={updateTextStyle} defaultValues={defaultValues}>
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.TextStyles}
          actions={[
            <Button
              key="save-text-style"
              type="submit"
              data-testid="save-text-style-button"
              isLoading={updateTextStyleMutation.isPending}
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      </TextStyleForm>
    </LoadingContainer>
  );
};
